import { ValueType } from "./valueTypes";
import { Input, Select, Radio, DatePicker, ColorPicker } from "antd";
import dayjs from "dayjs";
import React from "react";
import { getEnumAllowedCodes } from "./parseOptionsJson";

function normalizeInputType(valueType) {
    const type = String(valueType || "").trim().toUpperCase();
    switch (type) {
        case "NUMERIC":
            return ValueType.Numeric;
        case "STRING":
            return ValueType.String;
        case "BOOLEAN":
            return ValueType.Boolean;
        case "ENUM":
            return ValueType.Enum;
        case "ARRAY":
            return ValueType.Array;
        case "JSON":
            return ValueType.Json;
        case "DATE":
            return ValueType.Date;
        case "DURATION":
            return "DURATION";
        case "COLOR":
            return "COLOR";
        case "URL":
            return "URL";
        case "ASSET_REF":
            return "ASSET_REF";
        case "ENTITY_REF":
            return "ENTITY_REF";
        default:
            return valueType;
    }
}

function parseJsonArraySafe(raw) {
    try {
        const parsed = JSON.parse(raw || "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function colorToHex8(color) {
    // antd Color object -> canonical #RRGGBBAA expected by backend (см. Каталог типов значений)
    if (color && typeof color.toHex8String === "function") {
        return `#${color.toHex8String().replace(/^#/, "").toUpperCase()}`;
    }
    return "";
}

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm:ss";

/**
 * Рендерит контрол ввода/редактирования значения параметра по его valueTypeCode.
 * `options` — уже распарсенный объект parameter.optionsJson, нужен для ENUM (values/allowed, multiple) и DATE (mode).
 */
export default function ValueInput({ valueType, options, value, onChange, disabled }) {
    switch (normalizeInputType(valueType)) {
        case ValueType.Boolean:
            return (
                <Radio.Group
                    className="w-full"
                    value={String(value)}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="true">Да</Radio.Button>
                    <Radio.Button value="false">Нет</Radio.Button>
                </Radio.Group>
            );
        case ValueType.Enum: {
            const allowedCodes = getEnumAllowedCodes(options);
            const multiple = Boolean(options?.multiple);
            const selectOptions = allowedCodes.map((code) => ({ label: code, value: code }));
            if (multiple) {
                return (
                    <Select
                        mode="multiple"
                        className="w-full"
                        value={parseJsonArraySafe(value)}
                        onChange={(vals) => onChange(JSON.stringify(vals))}
                        placeholder="Выберите значения"
                        disabled={disabled}
                        options={selectOptions}
                    />
                );
            }
            return (
                <Select
                    className="w-full"
                    value={value || undefined}
                    onChange={(val) => onChange(val)}
                    placeholder="Выберите значение"
                    disabled={disabled}
                    options={selectOptions}
                />
            );
        }
        case ValueType.Date: {
            const mode = options?.mode || "date";
            if (mode === "time") {
                return (
                    <DatePicker
                        className="w-full"
                        picker="time"
                        value={value ? dayjs(value, TIME_FORMAT) : null}
                        onChange={(d) => onChange(d ? d.format(TIME_FORMAT) : "")}
                        disabled={disabled}
                    />
                );
            }
            if (mode === "datetime") {
                return (
                    <DatePicker
                        className="w-full"
                        showTime
                        value={value ? dayjs(value) : null}
                        onChange={(d) => onChange(d ? d.toISOString() : "")}
                        disabled={disabled}
                    />
                );
            }
            return (
                <DatePicker
                    className="w-full"
                    value={value ? dayjs(value, DATE_FORMAT) : null}
                    onChange={(d) => onChange(d ? d.format(DATE_FORMAT) : "")}
                    disabled={disabled}
                />
            );
        }
        case ValueType.Numeric:
            return <Input type="number" className="" value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
        case ValueType.Json:
            return (
                <Input.TextArea className="" rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder='{"k":1} или [1,2]' disabled={disabled} />
            );
        case ValueType.Array:
            return <Input className="" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="CSV: a,b,c или JSON-массив" disabled={disabled} />;
        case "DURATION":
            return <Input type="number" className="" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="миллисекунды" disabled={disabled} />;
        case "COLOR":
            return (
                <ColorPicker
                    showText
                    value={value || "#000000FF"}
                    disabled={disabled}
                />
            );
        case "URL":
            return <Input type="url" className="" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://example.com" disabled={disabled} />;
        case "ASSET_REF":
        case "ENTITY_REF":
            return <Input className=" font-mono" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="UUID" disabled={disabled} />;
        case ValueType.String:
        default:
            return <Input className="" value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    }
}
